interface PopoverObserver {
    disconnect(): void;
}

// Use a WeakMap to store observers for each popUp element
const popoverObserversMap = new WeakMap<HTMLElement, PopoverObserver[]>();

(window as any).popUpHelper = {
    /**
     * Positions and shows a popover element relative to a container element.
     * @param popUp The popover HTMLElement to show and position.
     * @param container The container HTMLElement to position relative to.
     * @param matchWidth should the popover match the size of the container.
     * @param alignment Alignment string: 'TopLeft', 'TopCenter', 'TopRight', 'BottomLeft', 'BottomCenter', 'BottomRight'
     */
    positionPopUp: function (popUp: HTMLElement, container: HTMLElement, matchWidth: boolean = false, alignment: string = "TopRight") {
        if (!popUp || !container) return;

        function clamp(number:number, min:number, max:number) {
            return Math.max(min, Math.min(number, max));
        }
        
        function updatePosition() {
            const rect = container.getBoundingClientRect();
            if (matchWidth) {
                popUp.style.width = `${rect.width}px`;
            }

            let top = 0, left = 0;
            const popWidth = popUp.querySelector("div").scrollWidth;
            const popHeight = popUp.querySelector("div").scrollHeight;
            
            switch (alignment) {
                case "TopLeft":
                    top = rect.top + window.scrollY - popHeight;
                    left = rect.left + window.scrollX;
                    break;
                case "TopCenter":
                    top = rect.top + window.scrollY - popHeight;
                    left = rect.left + window.scrollX + (rect.width / 2) - (popWidth / 2);
                    break;
                case "TopRight":
                    top = rect.top + window.scrollY - popHeight;
                    left = rect.right + window.scrollX - popWidth;
                    break;
                case "BottomLeft":
                    top = rect.bottom + window.scrollY;
                    left = rect.left + window.scrollX;
                    break;
                case "BottomCenter":
                    top = rect.bottom + window.scrollY;
                    left = rect.left + window.scrollX + (rect.width / 2) - (popWidth / 2);
                    break;
                case "BottomRight":
                    top = rect.bottom + window.scrollY;
                    left = rect.right + window.scrollX - popWidth;
                    break;
                default:
                    top = rect.top + window.scrollY;
                    left = rect.right + window.scrollX - popWidth;
            }

            // Ensure the popup stays within the viewport
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const scrollX = window.scrollX;
            const scrollY = window.scrollY;

            // Clamp left/right
            if (left < scrollX) {
                left = scrollX;
            } else if (left + popWidth > scrollX + viewportWidth) {
                left = scrollX + viewportWidth - popWidth;
            }
            // Clamp top/bottom
            if (top < scrollY) {
                top = scrollY;
            } else if (top + popHeight > scrollY + viewportHeight) {
                top = scrollY + viewportHeight - popHeight;
            }

            if (left < 0 || left > viewportWidth) {
                left = clamp(left, 0, viewportWidth)
                popUp.style.right = '0px';
            }
            
            if (top < 0 || top > viewportHeight) {
                top = clamp(top, 0, viewportHeight);
                popUp.style.bottom = '0px';
            }

            popUp.style.top = `${top}px`;
            popUp.style.left = `${left}px`;
        }

        // Initial position
        updatePosition();

        // Clean up any previous observers
        const prevObservers = popoverObserversMap.get(popUp);
        if (prevObservers && Array.isArray(prevObservers)) {
            prevObservers.forEach(obs => obs.disconnect && obs.disconnect());
        }
        const observers: PopoverObserver[] = [];

        // Observe container resize/move
        if (window.ResizeObserver) {
            const resizeObs = new ResizeObserver(updatePosition);
            resizeObs.observe(container);
            observers.push(resizeObs as PopoverObserver);
        }

        // Observe DOM mutations that might move the container
        const mutationObs = new MutationObserver(updatePosition);
        mutationObs.observe(document.body, { attributes: true, childList: true, subtree: true });
        observers.push(mutationObs as PopoverObserver);

        function onScrollOrResize() { updatePosition(); }
        window.addEventListener('scroll', onScrollOrResize, true);
        window.addEventListener('resize', onScrollOrResize, true);

        observers.push({
            disconnect: () => {
                window.removeEventListener('scroll', onScrollOrResize, true);
                window.removeEventListener('resize', onScrollOrResize, true);
            }
        });

        popoverObserversMap.set(popUp, observers);
    },

    /**
     * Disposes the popover by cleaning up observers and listeners.
     * @param popUp The popover HTMLElement to dispose.
     */
    disposePopUp: function (popUp: HTMLElement) {
        const observers = popoverObserversMap.get(popUp);
        if (observers && Array.isArray(observers)) {
            observers.forEach(obs => obs.disconnect && obs.disconnect());
        }
        popoverObserversMap.delete(popUp);
    }
};
